import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from '@phosphor-icons/react';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Button variant="ghost" className="mb-8 gap-2" onClick={() => navigate(-1)} data-testid="privacy-back-btn">
          <ArrowLeft size={18} />
          Voltar
        </Button>

        <div className="space-y-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-2">Política de Privacidade</h1>
            <p className="text-sm text-muted-foreground">Ultima atualização: 11 de março de 2026</p>
          </div>

          <div className="prose prose-sm max-w-none space-y-6 text-muted-foreground leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">1. Introdução</h2>
              <p>A Aretê ("nós", "nosso") valoriza e respeita a privacidade dos seus usuários. Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos seus dados pessoais, em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018) e demais legislações aplicáveis.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">2. Dados que Coletamos</h2>
              <p>Coletamos os seguintes tipos de dados:</p>
              <h3 className="text-lg font-medium text-foreground mt-4 mb-2">2.1. Dados de Cadastro</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Nome completo</li>
                <li>Endereço de email</li>
                <li>Senha (armazenada de forma criptografada)</li>
                <li>Número de WhatsApp (opcional)</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-4 mb-2">2.2. Dados Financeiros</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Transações (receitas e despesas)</li>
                <li>Metas financeiras</li>
                <li>Investimentos</li>
                <li>Categorias de gastos</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-4 mb-2">2.3. Dados de Uso do WhatsApp</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Mensagens de texto enviadas ao bot</li>
                <li>Mensagens de áudio (processadas e descartadas após transcrição)</li>
                <li>Imagens de recibos/notas fiscais (processadas e descartadas após análise)</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-4 mb-2">2.4. Dados Técnicos</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Endereço IP</li>
                <li>Tipo de navegador e dispositivo</li>
                <li>Dados de sessão e cookies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">3. Finalidade do Tratamento</h2>
              <p>Utilizamos seus dados para:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Prestação do serviço:</strong> gerenciar sua conta, processar transações financeiras e fornecer insights personalizados</li>
                <li><strong>Integração WhatsApp:</strong> processar comandos, transcrever áudios, analisar imagens e enviar notificações</li>
                <li><strong>Comunicação:</strong> enviar resumos diários, lembretes de tarefas e informações sobre sua conta</li>
                <li><strong>Melhoria do serviço:</strong> analisar padrões de uso para melhorar a experiência do usuário</li>
                <li><strong>Segurança:</strong> proteger contra acessos não autorizados e fraudes</li>
                <li><strong>Obrigações legais:</strong> cumprir requisitos legais e regulatórios</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">4. Base Legal para Tratamento (LGPD)</h2>
              <p>O tratamento dos seus dados é fundamentado nas seguintes bases legais:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Consentimento (Art. 7º, I):</strong> para integração com WhatsApp e envio de notificações</li>
                <li><strong>Execução de contrato (Art. 7º, V):</strong> para prestação dos serviços contratados</li>
                <li><strong>Legítimo interesse (Art. 7º, IX):</strong> para melhoria do serviço e segurança</li>
                <li><strong>Cumprimento de obrigação legal (Art. 7º, II):</strong> para obrigações fiscais e regulatórias</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">5. Compartilhamento de Dados</h2>
              <p>Seus dados podem ser compartilhados com:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Mercado Pago:</strong> para processamento de pagamentos (apenas dados necessários para cobrança)</li>
                <li><strong>Meta (WhatsApp Business API):</strong> para funcionamento do bot WhatsApp</li>
                <li><strong>OpenAI:</strong> para processamento de linguagem natural, transcrição de áudio e análise de imagens (dados são processados e não retidos pela OpenAI)</li>
              </ul>
              <p className="mt-2"><strong>Não vendemos, alugamos ou compartilhamos seus dados pessoais com terceiros para fins de marketing.</strong></p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">6. Armazenamento e Segurança</h2>
              <p>6.1. Seus dados são armazenados em servidores seguros com criptografia em trânsito (TLS/SSL) e em repouso.</p>
              <p>6.2. Senhas são armazenadas utilizando hashing seguro (bcrypt), sem possibilidade de recuperação em texto claro.</p>
              <p>6.3. Áudios e imagens enviados via WhatsApp são processados em tempo real e descartados imediatamente após o processamento. Apenas o resultado textual da transcrição ou análise é armazenado.</p>
              <p>6.4. Implementamos medidas técnicas e organizacionais adequadas para proteger seus dados contra acesso não autorizado, perda ou destruição.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">7. Seus Direitos (LGPD)</h2>
              <p>Conforme a LGPD, você tem direito a:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Acesso:</strong> solicitar uma cópia dos seus dados pessoais</li>
                <li><strong>Correção:</strong> corrigir dados incompletos ou desatualizados</li>
                <li><strong>Exclusão:</strong> solicitar a exclusão dos seus dados (anonimização)</li>
                <li><strong>Portabilidade:</strong> solicitar a transferência dos seus dados</li>
                <li><strong>Revogação do consentimento:</strong> revogar o consentimento a qualquer momento</li>
                <li><strong>Informação:</strong> ser informado sobre o compartilhamento dos seus dados</li>
                <li><strong>Oposição:</strong> opor-se ao tratamento em caso de descumprimento da LGPD</li>
              </ul>
              <p className="mt-2">Para exercer seus direitos, entre em contato pelo email: <a href="mailto:suporte@aretesystem.com.br" className="text-primary hover:underline">suporte@aretesystem.com.br</a></p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">8. Cookies</h2>
              <p>8.1. Utilizamos cookies de sessão para manter sua autenticação ativa durante o uso da Plataforma.</p>
              <p>8.2. Não utilizamos cookies de rastreamento de terceiros para fins publicitários.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">9. Retenção de Dados</h2>
              <p>9.1. Seus dados são retidos enquanto sua conta estiver ativa.</p>
              <p>9.2. Após o encerramento da conta, os dados são mantidos por 30 dias para possibilitar a recuperação, após o que são permanentemente excluídos.</p>
              <p>9.3. Dados necessários para cumprimento de obrigações legais podem ser retidos pelo prazo exigido por lei.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">10. Menores de Idade</h2>
              <p>A Plataforma não é destinada a menores de 18 anos. Não coletamos intencionalmente dados de menores. Caso identifiquemos tal situação, os dados serão excluídos imediatamente.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">11. Alterações nesta Política</h2>
              <p>Esta Política pode ser atualizada periodicamente. Notificaremos os usuários sobre alterações significativas por email ou por meio da Plataforma. O uso continuado dos serviços após a notificação constitui aceitação das alterações.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">12. Encarregado de Proteção de Dados (DPO)</h2>
              <p>Para questões relacionadas à proteção de dados pessoais, entre em contato com nosso Encarregado:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Email: <a href="mailto:suporte@aretesystem.com.br" className="text-primary hover:underline">suporte@aretesystem.com.br</a></li>
                <li>Site: <a href="https://aretesystem.com.br" className="text-primary hover:underline">aretesystem.com.br</a></li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">13. Autoridade Nacional de Proteção de Dados</h2>
              <p>Caso entenda que o tratamento dos seus dados pessoais viola a LGPD, você pode apresentar reclamação à Autoridade Nacional de Proteção de Dados (ANPD) pelo site: <a href="https://www.gov.br/anpd" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">www.gov.br/anpd</a></p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
